import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { extractPaywallDetail } from "@/lib/subscribe";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Clock, Euro, ArrowLeft, User, CalendarDays, Wrench, ShieldCheck, Rocket } from "lucide-react";
import { toast } from "sonner";
import ContactModal from "@/components/ContactModal";
import PaywallModal from "@/components/PaywallModal";
import StarRating from "@/components/StarRating";

export default function WorkerDetail() {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [wp, setWp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [featuring, setFeaturing] = useState(false);
  const [contact, setContact] = useState(null);
  const [paywall, setPaywall] = useState(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    api.get(`/workers/${id}`).then((r) => setWp(r.data)).catch(() => setWp(false)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="max-w-4xl mx-auto p-10 text-neutral-500 uppercase tracking-widest animate-pulse">Loading…</div>;
  if (!wp) return <div className="max-w-4xl mx-auto p-10">Profile not found. <Link to="/workers" className="text-amber-500">Back</Link></div>;

  const isOwner = user && user.id === wp.worker_id;
  const canContact = user && !isOwner;

  const contactWorker = async () => {
    if (!user) { navigate("/login"); return; }
    setApplying(true);
    try {
      const { data } = await api.post(`/apply/worker/${wp.id}`, { message });
      setContact(data);
      toast.success("Contact revealed");
    } catch (e) {
      const detail = extractPaywallDetail(e);
      if (detail) { setPaywall(detail); setApplying(false); return; }
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setApplying(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this profile?")) return;
    try {
      await api.delete(`/workers/${wp.id}`);
      toast.success("Profile removed");
      navigate("/dashboard");
    } catch { toast.error("Failed"); }
  };

  const buyVerified = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "worker_verified_badge",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
      setVerifying(false);
    }
  };

  const featureProfile = async () => {
    setFeaturing(true);
    // First-boost-free gift
    if (user && !user.free_boost_used) {
      try {
        const { data } = await api.post("/boost/free", { target_type: "worker", target_id: wp.id });
        setWp({ ...wp, boosted_until: data.boosted_until, is_boosted: true });
        toast.success("Featured for 7 days — free on us!");
        try { await refresh(); } catch (e) { console.warn("[WorkerDetail] refresh() failed after free boost", e); }
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to activate free boost");
      } finally {
        setFeaturing(false);
      }
      return;
    }
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "worker_boost_7d",
        origin_url: window.location.origin,
        metadata: { worker_profile_id: wp.id },
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to start feature");
      setFeaturing(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
      <Link to="/workers" data-testid="back-to-workers" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-400 hover:text-amber-500 mb-6">
        <ArrowLeft className="w-3 h-3" /> Back to crew
      </Link>
      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-neutral-900 border border-neutral-800 grid place-items-center">
              <User className="w-6 h-6 text-amber-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase">{wp.trade}</div>
                {wp.worker_verified && (
                  <span data-testid="verified-badge" className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500 text-neutral-950 text-[10px] font-bold uppercase tracking-widest">
                    <ShieldCheck className="w-3 h-3" /> Verified
                  </span>
                )}
              </div>
              <div className="display text-xl mt-1">{wp.worker_name}</div>
              <div className="mt-1">
                <StarRating value={wp.worker_avg_rating} count={wp.worker_review_count} size={12} testid="worker-rating" />
              </div>
            </div>
          </div>
          <h1 data-testid="worker-headline" className="display text-4xl md:text-5xl mt-2">{wp.headline}</h1>
          <div className="mt-4 flex flex-wrap gap-4 text-xs uppercase tracking-wide text-neutral-300">
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-amber-500" />{wp.county}</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-amber-500" />Available {wp.availability}</span>
            {wp.available_from && <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3 text-amber-500" />From {wp.available_from}</span>}
            <span className="flex items-center gap-1"><Wrench className="w-3 h-3 text-amber-500" />{wp.years_experience} yrs experience</span>
          </div>
          {wp.counties && wp.counties.length > 0 && (
            <div data-testid="worker-extra-counties" className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-neutral-500 mr-1">Will travel to</span>
              {wp.counties.map((c) => (
                <span key={c} className="text-[11px] uppercase tracking-wide bg-neutral-900 border border-amber-500/40 text-amber-500 px-2 py-1">{c}</span>
              ))}
            </div>
          )}
          <div className="mt-8 border-t border-neutral-800 pt-6">
            <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-2">About</div>
            <p data-testid="worker-description" className="text-neutral-200 leading-relaxed whitespace-pre-line">{wp.description}</p>
          </div>
        </div>

        <aside className="md:col-span-1">
          <div className="border border-neutral-800 bg-neutral-900 p-6 sticky top-20">
            <div className="text-xs font-bold uppercase tracking-widest text-neutral-500">Day Rate</div>
            <div className="display text-4xl text-amber-500 mt-1 flex items-center">
              {wp.day_rate ? (<><Euro className="w-6 h-6" />{wp.day_rate}</>) : "DOE"}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Per Day</div>

            {isOwner ? (
              <>
                {!wp.is_boosted && (
                  <button
                    data-testid="feature-worker-btn"
                    onClick={featureProfile}
                    disabled={featuring}
                    className="mt-6 w-full py-3 bg-amber-500 text-neutral-950 uppercase tracking-widest text-xs font-bold hover:bg-amber-400 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                  >
                    <Rocket className="w-4 h-4" />
                    {featuring ? "Redirecting…" : (user && !user.free_boost_used ? "Feature Profile — FREE (first gift)" : "Feature Profile — €9.99 · 7 days")}
                  </button>
                )}
                {!wp.is_boosted && user && !user.free_boost_used && (
                  <div className="mt-1 text-[10px] uppercase tracking-widest text-amber-500 text-center">Your first feature is on us · €0</div>
                )}
                {wp.is_boosted && (
                  <div data-testid="worker-featured-active" className="mt-6 border border-amber-500 bg-amber-500/10 p-3 text-center">
                    <div className="text-xs uppercase tracking-widest text-amber-500 font-bold">Featured active</div>
                    <div className="text-[10px] text-neutral-400 mt-1">Until {new Date(wp.boosted_until).toLocaleDateString()}</div>
                  </div>
                )}
                {!user.verified && (
                  <button
                    data-testid="buy-verified-btn"
                    onClick={buyVerified}
                    disabled={verifying}
                    className="mt-3 w-full py-3 border border-amber-500 text-amber-500 uppercase tracking-widest text-xs font-bold hover:bg-amber-500 hover:text-neutral-950 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                  >
                    <ShieldCheck className="w-4 h-4" />
                    {verifying ? "Redirecting…" : "Get Verified — €19"}
                  </button>
                )}
                {!user.verified && <div className="mt-1 text-[10px] uppercase tracking-widest text-neutral-500 text-center">One-off · Stand out to employers</div>}
                {user.verified && (
                  <div className="mt-3 border border-amber-500 bg-amber-500/10 p-3 text-center inline-flex items-center justify-center gap-2 w-full" data-testid="verified-active">
                    <ShieldCheck className="w-4 h-4 text-amber-500" />
                    <span className="text-xs font-bold uppercase tracking-widest text-amber-500">Verified account</span>
                  </div>
                )}
                <button data-testid="delete-profile-btn" onClick={remove} className="mt-3 w-full py-3 border border-red-800 text-red-300 hover:bg-red-950/50 uppercase tracking-widest text-xs font-bold transition-colors">
                  Delete Profile
                </button>
              </>
            ) : canContact ? (
              <>
                <textarea
                  data-testid="worker-contact-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Note to the worker (job details, dates)"
                  rows={3}
                  className="mt-6 w-full bg-neutral-950 border border-neutral-800 focus:border-amber-500 outline-none p-3 text-sm"
                />
                <button
                  data-testid="worker-contact-btn"
                  onClick={contactWorker}
                  disabled={applying}
                  className="mt-3 w-full py-3 bg-amber-500 text-neutral-950 uppercase tracking-widest text-xs font-bold hover:bg-amber-400 transition-colors disabled:opacity-60"
                >
                  {applying ? "Sending…" : "Reveal Contact"}
                </button>
              </>
            ) : (
              <Link to="/login" data-testid="worker-contact-login-btn" className="mt-6 block text-center w-full py-3 bg-amber-500 text-neutral-950 uppercase tracking-widest text-xs font-bold hover:bg-amber-400 transition-colors">
                Log in to Contact
              </Link>
            )}
          </div>
        </aside>
      </div>

      {contact && (
        <ContactModal
          contact={contact}
          onClose={() => setContact(null)}
          title="Contact revealed"
          subtitle={`Regarding: ${wp.headline}`}
        />
      )}
      {paywall && <PaywallModal detail={paywall} onClose={() => setPaywall(null)} />}
    </div>
  );
}
